
// ==========================================
// 7. src/components/sections/SolutionSection.js
// ==========================================
import React from 'react';
import { Truck, Database, Smartphone, Zap, Users, Shield } from 'lucide-react';
import SectionHeader from '../common/SectionHeader';

const SolutionCard = ({ gradient, icon: Icon, iconBg, title, description, replaces, replacesColor, borderColor }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-xl p-6 border ${borderColor} hover:shadow-xl transition`}>
    <div className={`w-12 h-12 ${iconBg} rounded-lg flex items-center justify-center mb-4`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 mb-3">{description}</p>
    <p className={`text-sm font-medium ${replacesColor}`}>
      Replaces: {replaces}
    </p>
  </div>
);

const SolutionSection = () => {
  const solutions = [
    {
      gradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-100',
      icon: Truck,
      iconBg: 'bg-blue-600',
      title: 'Transportation Management System',
      description: 'Tender→POD workflows, appointments, documents, exceptions, and customer comms—centralized and API-ready.',
      replaces: 'Manual dispatch boards',
      replacesColor: 'text-blue-600'
    },
    {
      gradient: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-100',
      icon: Database,
      iconBg: 'bg-green-600',
      title: 'Data Bridge (Any-to-Any Data)',
      description: 'Translate and transport data reliably (X12 EDI, JSON, XML, CSV) over AS2, APIs, SFTP with validation and audit trails.',
      replaces: 'Manual data processing',
      replacesColor: 'text-green-600'
    },
    {
      gradient: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-100',
      icon: Smartphone,
      iconBg: 'bg-purple-600',
      title: 'SYBR Track (Mobile)',
      description: 'Live driver & asset visibility, on-the-go workflows, and status events that feed ops, customers, and billing.',
      replaces: 'Phone tag & check calls',
      replacesColor: 'text-purple-600'
    },
    {
      gradient: 'from-orange-50 to-red-50',
      borderColor: 'border-orange-100',
      icon: Zap,
      iconBg: 'bg-orange-600',
      title: 'Transportation Intelligence™',
      description: 'AI + rules + real-time signals ensure freight finds the right asset—cutting the manual juggling and slashing time-to-accept.',
      replaces: 'Dispatch and spreadsheet matching',
      replacesColor: 'text-orange-600'
    },
    {
      gradient: 'from-cyan-50 to-blue-50',
      borderColor: 'border-cyan-100',
      icon: Users,
      iconBg: 'bg-cyan-600',
      title: 'Dispatch as a Service',
      description: 'A managed desk that extends your team with standardized best practices and 24/7 continuity.',
      replaces: 'Overtime & temp staff',
      replacesColor: 'text-cyan-600'
    },
    {
      gradient: 'from-indigo-50 to-purple-50',
      borderColor: 'border-indigo-100',
      icon: Shield,
      iconBg: 'bg-indigo-600',
      title: 'Accounting & Compliance',
      description: 'Hooks to billing, payouts, taxes, customer and contract compliance—so ops and accounting stay in lockstep.',
      replaces: 'Manual reconciliation',
      replacesColor: 'text-indigo-600'
    }
  ];

  return (
    <section id="solution" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader 
          title="The Complete Platform That Actually Works"
          subtitle="Six core components that connect your entire operation—from tender to POD"
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution, index) => (
            <SolutionCard key={index} {...solution} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;